import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { popPendingInvite } from "@/pages/JoinHousehold";

/**
 * AuthCallback — invoked when the URL fragment carries a session_id from
 * Emergent Auth. Exchanges it with our backend, which sets an httpOnly
 * session cookie, then routes to /app (which will bounce to /app/onboarding
 * if the household profile is not yet complete).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setError("Missing session id. Try signing in again.");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post(
          "/auth/session",
          { session_id: sessionId },
          { withCredentials: true }
        );
        // clear the hash so refresh doesn't retrigger
        window.history.replaceState(null, "", window.location.pathname);

        toast.success(`Welcome, ${data.user?.name?.split(" ")[0] || "pilot"}.`);

        // If we came from a household invite link, redeem it now.
        const pendingToken = popPendingInvite();
        if (pendingToken) {
          try {
            const { data: acceptData } = await api.post("/household/invites/accept", {
              token: pendingToken,
            });
            toast.success("You’ve joined the household.");
            navigate("/app/tower", { replace: true, state: { user: data.user, joined: acceptData } });
            return;
          } catch (e) {
            const msg = e?.response?.data?.detail || "Invitation could not be accepted.";
            toast.error(typeof msg === "string" ? msg : "Invitation could not be accepted.");
            // fall through — route them to onboarding/tower per usual
          }
        }

        if (data.user?.profile_complete) {
          navigate("/app/tower", { replace: true, state: { user: data.user } });
        } else {
          navigate("/app/onboarding", { replace: true, state: { user: data.user } });
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || "Sign-in failed. Please retry.";
        setError(typeof msg === "string" ? msg : "Sign-in failed.");
      }
    })();
  }, [navigate]);

  return (
    <div
      data-testid="auth-callback-page"
      className="flex min-h-screen items-center justify-center bg-obsidian"
    >
      <div className="text-center">
        <div className="mx-auto flex h-8 w-8 items-center justify-center border border-teal/70">
          <span className="h-2 w-2 bg-teal animate-pulse-soft" />
        </div>
        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.26em] text-mist">
          {error ? "Clearance failed" : "Verifying clearance…"}
        </p>
        {error && (
          <>
            <p className="mt-3 max-w-sm text-sm text-ink">{error}</p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="mt-6 border border-rule px-4 py-2 font-mono text-[11px] uppercase tracking-[0.22em] text-mist hover:text-ink"
            >
              Back to sign-in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
